import { Events, on } from '../../../engine/core/events.js';
import { getSyncPauses } from '../../../engine/sync/sync-pause.js';
import { t } from '../../../shared/i18n/index.js';

const REASONS = ['protocol', 'key', 'conflict', 'unreadable', 'structure'];
const reasonText = (reason) => t(`app.syncPaused.reason.${REASONS.includes(reason) ? reason : 'unreadable'}`);

/**
 * @param {Object} opts
 * @param {(docId: string) => string} opts.getTitle
 * @param {(pause: Object) => Promise<void>} opts.onRetry
 * @param {(pause: Object) => Promise<void>} opts.onExport
 * @param {(pause: Object) => boolean} [opts.canSetAside] - true when this user
 *   (owner/admin) can set aside the updates nobody can read (S-H2)
 * @param {(pause: Object) => Promise<void>} [opts.onSetAside]
 */
export function initSyncPausedNotice({ getTitle, onRetry, onExport, canSetAside = () => false, onSetAside }) {
  if (document.getElementById('sync-paused-notice')) return;
  const notice = document.createElement('details');
  notice.id = 'sync-paused-notice';
  notice.open = true;
  notice.hidden = true;
  const summary = document.createElement('summary');
  summary.textContent = t('app.syncPaused.title');
  const message = document.createElement('p');
  message.textContent = t('app.syncPaused.message');
  const list = document.createElement('div');
  notice.append(summary, message, list);
  document.body.appendChild(notice);
  const rows = new Map();
  const render = () => {
    const pauses = getSyncPauses();
    notice.hidden = !pauses.length;
    summary.textContent = pauses.length > 1 ? t('app.syncPaused.titleCount', { count: pauses.length }) : t('app.syncPaused.title');
    const current = new Set(pauses.map(p => `${p.scope}:${p.id}`));
    for (const [id, row] of rows) if (!current.has(id)) { row.remove(); rows.delete(id); }
    for (const pause of pauses) {
      const id = `${pause.scope}:${pause.id}`;
      let row = rows.get(id);
      if (!row) {
        row = document.createElement('div');
        row.className = 'sync-paused-item';
        row.dataset.scope = pause.scope;
        const title = document.createElement('strong');
        const reason = document.createElement('span');
        const status = document.createElement('span');
        status.className = 'sync-paused-status';
        status.setAttribute('role', 'status');
        const actions = document.createElement('div');
        actions.className = 'sync-paused-actions';
        for (const [kind, action] of [['retry', onRetry], ['export', onExport]]) {
          const retry = kind === 'retry';
          const button = document.createElement('button');
          button.type = 'button'; button.textContent = retry ? t('common.retry') : t('common.export');
          button.addEventListener('click', async () => {
            button.disabled = true;
            status.textContent = retry ? t('app.syncPaused.checking') : t('app.syncPaused.preparingBackup');
            try {
              await action(pause);
              status.textContent = retry && getSyncPauses().some(p => `${p.scope}:${p.id}` === id)
                ? t('app.syncPaused.stillPaused') : '';
            } catch {
              status.textContent = retry ? t('app.syncPaused.retryFailed') : t('app.syncPaused.exportFailed');
            } finally { button.disabled = false; }
          });
          actions.appendChild(button);
        }
        if (onSetAside) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'sync-paused-set-aside';
          button.textContent = t('app.setAside.title');
          button.title = t('app.syncPaused.setAsideHint');
          button.addEventListener('click', async () => {
            button.disabled = true;
            status.textContent = t('app.syncPaused.settingAside');
            try {
              await onSetAside(pause);
              status.textContent = '';
            } catch (err) {
              status.textContent = err?.message || t('app.syncPaused.setAsideFailed');
            } finally { button.disabled = false; }
          });
          actions.appendChild(button);
        }
        row.append(title, reason, actions, status);
        list.appendChild(row); rows.set(id, row);
      }
      row.querySelector('strong').textContent = pause.scope === 'vault' ? t('app.syncPaused.vaultIndex') : getTitle(pause.id) || t('common.map');
      row.querySelector('span').textContent = reasonText(pause.reason);
      const setAside = row.querySelector('.sync-paused-set-aside');
      if (setAside) setAside.hidden = !(pause.scope === 'map' && pause.reason === 'unreadable' && canSetAside(pause));
    }
  };
  on(Events.SYNC_PAUSED, render);
  on(Events.SYNC_PAUSE_CHANGED, render);
  on(Events.VAULT_INDEX_UPDATED, render);
  render();
}
